import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Archive, ArchiveRestore, MessageSquarePlus, Loader2, Inbox, ArchiveX,
} from 'lucide-react';
import {
  aiAssistantService,
  type ConversationSummary,
} from '@/services/aiAssistantService';

/**
 * Left rail of the AI assistant page — the user's conversation history. Two
 * tabs: Active (default) and Archived (the archive bin). Selecting a row
 * loads that conversation into the chat panel; the New button starts a
 * fresh conversation (`null` selection).
 *
 * Archive replaces delete: a user can hide a conversation but the audit
 * trail of AI activity (including any schedules attached to it) stays.
 * Archiving auto-disables every schedule on the conversation; un-archiving
 * deliberately does NOT re-enable them.
 */
interface Props {
  /** Currently-open conversation id (`null` = a fresh, unsaved conversation). */
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Bumped by the chat panel after a new conversation is committed so the
   *  rail refreshes and shows the new row. */
  refreshKey: number;
}

type Tab = 'active' | 'archived';

export const ConversationHistoryRail: React.FC<Props> = ({ selectedId, onSelect, refreshKey }) => {
  const [tab, setTab] = useState<Tab>('active');
  const [rows, setRows] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Monotonic request id — a previous in-flight fetch must NOT overwrite the
  // current tab's state if it resolves after a faster, newer fetch. Without
  // this guard, rapid Active ↔ Archive toggling shows stale rows for the
  // tab the user is no longer on.
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const myReq = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const fetched = await aiAssistantService.listConversations(tab === 'archived', 50);
      if (myReq !== reqRef.current) return;   // a newer load() is in flight
      setRows(fetched);
    } catch (e) {
      if (myReq !== reqRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load conversations');
    } finally {
      if (myReq === reqRef.current) setLoading(false);
    }
  }, [tab]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  // Track the current tab + selection in refs so the archive/unarchive
  // closures read the LATEST values, not whatever was captured at the time
  // the callback was created. Without this, a tab switch or row click
  // landing while a request is in flight would leave the completion path
  // acting on a stale snapshot — fetching the wrong tab, or clearing a
  // newer selection.
  const tabRef = useRef<Tab>(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);
  const selectedIdRef = useRef(selectedId);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  /** Reload the CURRENT tab — not whatever tab was selected when the
   *  caller's closure was created. Archive/unarchive callbacks await this
   *  after a long network round-trip during which the user may have toggled
   *  tabs, and a stale tab fetch would overwrite the rows on the tab the
   *  user is actually looking at. */
  const reloadCurrentTab = useCallback(async () => {
    const myReq = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const fetched = await aiAssistantService.listConversations(tabRef.current === 'archived', 50);
      if (myReq !== reqRef.current) return;
      setRows(fetched);
    } catch (e) {
      if (myReq !== reqRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load conversations');
    } finally {
      if (myReq === reqRef.current) setLoading(false);
    }
  }, []);

  const archive = async (id: string) => {
    if (busyId) return;
    setBusyId(id);
    try {
      await aiAssistantService.archiveConversation(id);
      // If the OPEN conversation was archived, drop selection — but only
      // if the user hasn't already navigated to a different one in the
      // meantime (selectedIdRef gives the latest value, not the one
      // captured when archive() started).
      if (selectedIdRef.current === id) onSelect(null);
      await reloadCurrentTab();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Archive failed');
    } finally {
      setBusyId(null);
    }
  };

  const unarchive = async (id: string) => {
    if (busyId) return;
    setBusyId(id);
    try {
      await aiAssistantService.unarchiveConversation(id);
      await reloadCurrentTab();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Un-archive failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <aside className="flex flex-col w-full lg:w-72 shrink-0 max-h-[40vh] lg:max-h-none border-b lg:border-b-0 lg:border-r bg-card">
      <div className="p-2 flex items-center gap-2">
        <Button
          size="sm"
          variant="default"
          className="flex-1 h-8 text-xs"
          // Switch back to Active first — a new thread is active by
          // definition, so clicking 'New conversation' while looking at
          // the Archive bin would otherwise leave the rail showing
          // archived rows and the user couldn't find the thread they
          // just started.
          onClick={() => {
            if (tab !== 'active') setTab('active');
            onSelect(null);
          }}>
          <MessageSquarePlus className="h-3.5 w-3.5 mr-1.5" />
          New conversation
        </Button>
      </div>

      <div className="grid grid-cols-2 border-y bg-muted/30 text-xs">
        <button
          className={`py-2 flex items-center justify-center gap-1.5 ${
            tab === 'active' ? 'bg-card font-medium' : 'text-muted-foreground hover:bg-muted'
          }`}
          onClick={() => setTab('active')}>
          <Inbox className="h-3.5 w-3.5" /> Active
        </button>
        <button
          className={`py-2 flex items-center justify-center gap-1.5 ${
            tab === 'archived' ? 'bg-card font-medium' : 'text-muted-foreground hover:bg-muted'
          }`}
          onClick={() => setTab('archived')}>
          <ArchiveX className="h-3.5 w-3.5" /> Archive
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {error && <p className="text-xs text-destructive px-3 py-2">{error}</p>}
        {loading && (
          <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <p className="text-xs text-muted-foreground px-3 py-4 text-center">
            {tab === 'active' ? 'No conversations yet.' : 'Nothing archived.'}
          </p>
        )}
        <ul className="text-sm">
          {rows.map((c) => {
            const isSelected = selectedId === c.id;
            return (
              <li
                key={c.id}
                className={`group flex items-start gap-1 px-2 py-2 cursor-pointer border-l-2 ${
                  isSelected
                    ? 'border-l-primary bg-primary/5'
                    : 'border-l-transparent hover:bg-muted/50'
                }`}
                onClick={() => onSelect(c.id)}>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium text-[13px]">
                    {c.title?.trim() || '(untitled)'}
                  </div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <span className="capitalize">{c.context}</span>
                    <span>·</span>
                    <span>{formatRelative(c.updatedAt)}</span>
                  </div>
                </div>
                <button
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
                  title={tab === 'active' ? 'Archive' : 'Un-archive'}
                  disabled={busyId === c.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    void (tab === 'active' ? archive(c.id) : unarchive(c.id));
                  }}>
                  {busyId === c.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : tab === 'active' ? (
                    <Archive className="h-3.5 w-3.5" />
                  ) : (
                    <ArchiveRestore className="h-3.5 w-3.5" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
};

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}

export default ConversationHistoryRail;
